// src/admin/PlatformAnalytics.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  CalendarDays,
  Download,
  RefreshCw,
  ArrowRight,
  Filter,
  ChevronDown,
} from 'lucide-react';

// Dashboard components for analytics
const PlatformAnalytics = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');
  const [chartType, setChartType] = useState('signups');
  const [filterOpen, setFilterOpen] = useState(false);
  const [metricFilters, setMetricFilters] = useState({
    showRevenue: true,
    showSignups: true,
    showChurn: true,
    showActiveUsers: true,
  });

  // Sample data for the various charts
  const chartData = useMemo(
    () => ({
      signups: [
        { date: '2025-04-20', count: 3 },
        { date: '2025-04-21', count: 5 },
        { date: '2025-04-22', count: 2 },
        { date: '2025-04-23', count: 4 },
        { date: '2025-04-24', count: 6 },
        { date: '2025-04-25', count: 3 },
        { date: '2025-04-26', count: 2 },
        { date: '2025-04-27', count: 4 },
        { date: '2025-04-28', count: 7 },
        { date: '2025-04-29', count: 5 },
        { date: '2025-04-30', count: 8 },
        { date: '2025-05-01', count: 6 },
        { date: '2025-05-02', count: 4 },
        { date: '2025-05-03', count: 5 },
        { date: '2025-05-04', count: 3 },
        { date: '2025-05-05', count: 7 },
        { date: '2025-05-06', count: 6 },
        { date: '2025-05-07', count: 9 },
        { date: '2025-05-08', count: 8 },
        { date: '2025-05-09', count: 10 },
        { date: '2025-05-10', count: 7 },
        { date: '2025-05-11', count: 5 },
        { date: '2025-05-12', count: 6 },
        { date: '2025-05-13', count: 8 },
        { date: '2025-05-14', count: 9 },
        { date: '2025-05-15', count: 7 },
        { date: '2025-05-16', count: 8 },
        { date: '2025-05-17', count: 6 },
        { date: '2025-05-18', count: 5 },
        { date: '2025-05-19', count: 10 },
        { date: '2025-05-20', count: 12 },
      ],
      revenue: [
        { date: '2025-04-20', amount: 320 },
        { date: '2025-04-21', amount: 450 },
        { date: '2025-04-22', amount: 280 },
        { date: '2025-04-23', amount: 390 },
        { date: '2025-04-24', amount: 520 },
        { date: '2025-04-25', amount: 340 },
        { date: '2025-04-26', amount: 270 },
        { date: '2025-04-27', amount: 410 },
        { date: '2025-04-28', amount: 580 },
        { date: '2025-04-29', amount: 490 },
        { date: '2025-04-30', amount: 620 },
        { date: '2025-05-01', amount: 530 },
        { date: '2025-05-02', amount: 420 },
        { date: '2025-05-03', amount: 480 },
        { date: '2025-05-04', amount: 350 },
        { date: '2025-05-05', amount: 590 },
        { date: '2025-05-06', amount: 520 },
        { date: '2025-05-07', amount: 680 },
        { date: '2025-05-08', amount: 630 },
        { date: '2025-05-09', amount: 710 },
        { date: '2025-05-10', amount: 570 },
        { date: '2025-05-11', amount: 490 },
        { date: '2025-05-12', amount: 540 },
        { date: '2025-05-13', amount: 650 },
        { date: '2025-05-14', amount: 700 },
        { date: '2025-05-15', amount: 580 },
        { date: '2025-05-16', amount: 620 },
        { date: '2025-05-17', amount: 520 },
        { date: '2025-05-18', amount: 470 },
        { date: '2025-05-19', amount: 740 },
        { date: '2025-05-20', amount: 820 },
      ],
      activeUsers: [
        { date: '2025-04-20', count: 42 },
        { date: '2025-04-21', count: 45 },
        { date: '2025-04-22', count: 47 },
        { date: '2025-04-23', count: 48 },
        { date: '2025-04-24', count: 51 },
        { date: '2025-04-25', count: 53 },
        { date: '2025-04-26', count: 52 },
        { date: '2025-04-27', count: 54 },
        { date: '2025-04-28', count: 57 },
        { date: '2025-04-29', count: 59 },
        { date: '2025-04-30', count: 62 },
        { date: '2025-05-01', count: 63 },
        { date: '2025-05-02', count: 62 },
        { date: '2025-05-03', count: 64 },
        { date: '2025-05-04', count: 63 },
        { date: '2025-05-05', count: 65 },
        { date: '2025-05-06', count: 68 },
        { date: '2025-05-07', count: 72 },
        { date: '2025-05-08', count: 74 },
        { date: '2025-05-09', count: 77 },
        { date: '2025-05-10', count: 76 },
        { date: '2025-05-11', count: 75 },
        { date: '2025-05-12', count: 78 },
        { date: '2025-05-13', count: 82 },
        { date: '2025-05-14', count: 85 },
        { date: '2025-05-15', count: 83 },
        { date: '2025-05-16', count: 85 },
        { date: '2025-05-17', count: 84 },
        { date: '2025-05-18', count: 83 },
        { date: '2025-05-19', count: 87 },
        { date: '2025-05-20', count: 92 },
      ],
      churn: [
        { date: '2025-04-20', percentage: 2.1 },
        { date: '2025-04-21', percentage: 1.8 },
        { date: '2025-04-22', percentage: 2.2 },
        { date: '2025-04-23', percentage: 1.9 },
        { date: '2025-04-24', percentage: 1.7 },
        { date: '2025-04-25', percentage: 2.0 },
        { date: '2025-04-26', percentage: 2.3 },
        { date: '2025-04-27', percentage: 1.8 },
        { date: '2025-04-28', percentage: 1.5 },
        { date: '2025-04-29', percentage: 1.7 },
        { date: '2025-04-30', percentage: 1.4 },
        { date: '2025-05-01', percentage: 2.1 },
        { date: '2025-05-02', percentage: 2.3 },
        { date: '2025-05-03', percentage: 2.0 },
        { date: '2025-05-04', percentage: 1.9 },
        { date: '2025-05-05', percentage: 1.6 },
        { date: '2025-05-06', percentage: 1.4 },
        { date: '2025-05-07', percentage: 1.3 },
        { date: '2025-05-08', percentage: 1.5 },
        { date: '2025-05-09', percentage: 1.2 },
        { date: '2025-05-10', percentage: 1.4 },
        { date: '2025-05-11', percentage: 1.5 },
        { date: '2025-05-12', percentage: 1.3 },
        { date: '2025-05-13', percentage: 1.1 },
        { date: '2025-05-14', percentage: 1.0 },
        { date: '2025-05-15', percentage: 1.2 },
        { date: '2025-05-16', percentage: 1.3 },
        { date: '2025-05-17', percentage: 1.2 },
        { date: '2025-05-18', percentage: 1.4 },
        { date: '2025-05-19', percentage: 1.1 },
        { date: '2025-05-20', percentage: 0.9 },
      ],
      portals: [
        { date: '2025-04-20', count: 128 },
        { date: '2025-04-21', count: 130 },
        { date: '2025-04-22', count: 132 },
        { date: '2025-04-23', count: 135 },
        { date: '2025-04-24', count: 139 },
        { date: '2025-04-25', count: 142 },
        { date: '2025-04-26', count: 142 },
        { date: '2025-04-27', count: 145 },
        { date: '2025-04-28', count: 148 },
        { date: '2025-04-29', count: 152 },
        { date: '2025-04-30', count: 156 },
        { date: '2025-05-01', count: 159 },
        { date: '2025-05-02', count: 161 },
        { date: '2025-05-03', count: 163 },
        { date: '2025-05-04', count: 165 },
        { date: '2025-05-05', count: 168 },
        { date: '2025-05-06', count: 171 },
        { date: '2025-05-07', count: 176 },
        { date: '2025-05-08', count: 179 },
        { date: '2025-05-09', count: 183 },
        { date: '2025-05-10', count: 186 },
        { date: '2025-05-11', count: 187 },
        { date: '2025-05-12', count: 190 },
        { date: '2025-05-13', count: 194 },
        { date: '2025-05-14', count: 198 },
        { date: '2025-05-15', count: 199 },
        { date: '2025-05-16', count: 201 },
        { date: '2025-05-17', count: 201 },
        { date: '2025-05-18', count: 202 },
        { date: '2025-05-19', count: 204 },
        { date: '2025-05-20', count: 208 },
      ],
    }),
    []
  ); // Empty dependency array means this will only be calculated once

  // KPI Cards data
  const kpiData = {
    totalRevenue: {
      value: '$7,849',
      change: '+12.4%',
      positive: true,
      label: 'Monthly Revenue',
    },
    mrr: {
      value: '$7,420',
      change: '+8.3%',
      positive: true,
      label: 'Monthly Recurring Revenue',
    },
    businesses: {
      value: '92',
      change: '+15.2%',
      positive: true,
      label: 'Active Businesses',
    },
    churn: {
      value: '1.2%',
      change: '-0.3%',
      positive: true,
      label: 'Monthly Churn Rate',
    },
  };

  // Plan distribution data
  const planDistributionData = [
    { name: 'Starter', value: 54, color: 'bg-blue-500' },
    { name: 'Professional', value: 32, color: 'bg-indigo-500' },
    { name: 'Business', value: 14, color: 'bg-purple-500' },
  ];

  // Fetch the analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        // const response = await fetch(`/.netlify/functions/get-analytics?range=${dateRange}`);
        // const data = await response.json();
        // setData(data);

        // For demo purposes, we'll simulate an API delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Just use our predefined data
        setData(chartData);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange, chartData]); // Added chartData as a dependency

  // Generate the chart based on selected type and data
  const renderChart = () => {
    if (!data) return null;

    const selectedData = data[chartType];
    if (!selectedData) return null;

    const max = Math.max(
      ...selectedData.map((d) =>
        chartType === 'revenue'
          ? d.amount
          : chartType === 'churn'
          ? d.percentage
          : d.count
      )
    );

    return (
      <div className="h-80 mt-4 relative">
        <div className="absolute inset-0 flex items-end space-x-1">
          {selectedData.map((d, i) => {
            const value =
              chartType === 'revenue'
                ? d.amount
                : chartType === 'churn'
                ? d.percentage
                : d.count;
            const percentage = (value / max) * 100;

            // Only show every 4th label on mobile for better spacing
            const showLabel = i % 4 === 0;

            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div
                  className={`w-full ${
                    chartType === 'revenue'
                      ? 'bg-green-500'
                      : chartType === 'signups'
                      ? 'bg-blue-500'
                      : chartType === 'churn'
                      ? 'bg-red-500'
                      : chartType === 'activeUsers'
                      ? 'bg-indigo-500'
                      : 'bg-purple-500'
                  } rounded-t`}
                  style={{ height: `${percentage}%` }}
                ></div>
                {showLabel && (
                  <div className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-top-left">
                    {d.date.split('-')[2]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  // Handle chart type change
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  // Toggle a metric filter
  const toggleMetricFilter = (filter) => {
    setMetricFilters({
      ...metricFilters,
      [filter]: !metricFilters[filter],
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header and Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">
            Platform Analytics
          </h1>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {/* Date range picker */}
            <div className="relative">
              <div
                className="flex items-center justify-between bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 cursor-pointer"
                onClick={() => setFilterOpen(!filterOpen)} // Temporarily using this to toggle filter instead of separate date dropdown
              >
                <CalendarDays className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-700">
                  {dateRange === '7days'
                    ? 'Last 7 days'
                    : dateRange === '30days'
                    ? 'Last 30 days'
                    : dateRange === '90days'
                    ? 'Last 90 days'
                    : 'Last 12 months'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
              </div>

              {/* Date range quick select buttons - Added below the dropdown */}
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  className={`px-2 py-1 text-xs rounded-md ${
                    dateRange === '7days'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => handleDateRangeChange('7days')}
                >
                  7 days
                </button>
                <button
                  className={`px-2 py-1 text-xs rounded-md ${
                    dateRange === '30days'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => handleDateRangeChange('30days')}
                >
                  30 days
                </button>
                <button
                  className={`px-2 py-1 text-xs rounded-md ${
                    dateRange === '90days'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => handleDateRangeChange('90days')}
                >
                  90 days
                </button>
              </div>
            </div>

            {/* Export button */}
            <button className="bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 flex items-center justify-center text-sm text-gray-700 hover:bg-gray-50">
              <Download className="h-5 w-5 text-gray-400 mr-2" />
              Export Report
            </button>

            {/* Filter button */}
            <button
              className="bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 flex items-center justify-center text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <Filter className="h-5 w-5 text-gray-400 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filter panel (conditionally shown) */}
        {filterOpen && (
          <div className="bg-white p-4 rounded-lg shadow border mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Filter Metrics
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center">
                <input
                  id="filter-revenue"
                  type="checkbox"
                  checked={metricFilters.showRevenue}
                  onChange={() => toggleMetricFilter('showRevenue')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="filter-revenue"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Revenue
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="filter-signups"
                  type="checkbox"
                  checked={metricFilters.showSignups}
                  onChange={() => toggleMetricFilter('showSignups')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="filter-signups"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Signups
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="filter-churn"
                  type="checkbox"
                  checked={metricFilters.showChurn}
                  onChange={() => toggleMetricFilter('showChurn')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="filter-churn"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Churn Rate
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="filter-active-users"
                  type="checkbox"
                  checked={metricFilters.showActiveUsers}
                  onChange={() => toggleMetricFilter('showActiveUsers')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="filter-active-users"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Active Users
                </label>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="h-12 w-12 text-indigo-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {Object.entries(kpiData).map(([key, data]) => (
                <div
                  className="bg-white rounded-lg shadow border p-6"
                  key={key}
                >
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-500 font-medium">
                      {data.label}
                    </p>
                    <p className="text-3xl font-bold mt-2">{data.value}</p>
                    <div
                      className={`flex items-center mt-2 ${
                        data.positive ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      <span>{data.change}</span>
                      <span className="text-xs ml-1">vs. last month</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Main Chart */}
            <div className="bg-white rounded-lg shadow border p-6 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {chartType === 'revenue'
                    ? 'Revenue'
                    : chartType === 'signups'
                    ? 'New Signups'
                    : chartType === 'churn'
                    ? 'Churn Rate'
                    : chartType === 'activeUsers'
                    ? 'Active Users'
                    : 'Client Portals'}
                </h2>

                <div className="flex mt-3 sm:mt-0">
                  <button
                    className={`px-3 py-1 text-sm rounded-md mr-2 ${
                      chartType === 'signups'
                        ? 'bg-blue-100 text-blue-800'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => handleChartTypeChange('signups')}
                  >
                    Signups
                  </button>
                  <button
                    className={`px-3 py-1 text-sm rounded-md mr-2 ${
                      chartType === 'revenue'
                        ? 'bg-green-100 text-green-800'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => handleChartTypeChange('revenue')}
                  >
                    Revenue
                  </button>
                  <button
                    className={`px-3 py-1 text-sm rounded-md mr-2 ${
                      chartType === 'activeUsers'
                        ? 'bg-indigo-100 text-indigo-800'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => handleChartTypeChange('activeUsers')}
                  >
                    Active Users
                  </button>
                  <button
                    className={`px-3 py-1 text-sm rounded-md ${
                      chartType === 'churn'
                        ? 'bg-red-100 text-red-800'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => handleChartTypeChange('churn')}
                  >
                    Churn
                  </button>
                </div>
              </div>

              {/* Chart Container */}
              {renderChart()}

              <div className="mt-4 flex justify-center">
                <div className="text-xs text-gray-500">
                  {dateRange === '30days'
                    ? 'Last 30 days'
                    : dateRange === '7days'
                    ? 'Last 7 days'
                    : 'Last 90 days'}
                </div>
              </div>
            </div>

            {/* Additional Metrics and Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Plan Distribution */}
              <div className="bg-white rounded-lg shadow border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Plan Distribution
                </h2>

                <div className="space-y-4">
                  {planDistributionData.map((plan, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {plan.name}
                        </span>
                        <span className="text-sm text-gray-500">
                          {plan.value}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className={`${plan.color} h-2.5 rounded-full`}
                          style={{ width: `${plan.value}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      Average Revenue Per User
                    </span>
                    <span className="text-lg font-semibold">$86.50</span>
                  </div>
                </div>
              </div>

              {/* Conversion Metrics */}
              <div className="bg-white rounded-lg shadow border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Conversion Metrics
                </h2>

                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Trial to Paid Conversion
                      </span>
                      <span className="text-sm text-gray-500">38%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-500 h-2.5 rounded-full"
                        style={{ width: '38%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Upgrade Rate
                      </span>
                      <span className="text-sm text-gray-500">22%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-green-500 h-2.5 rounded-full"
                        style={{ width: '22%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Enterprise Conversion
                      </span>
                      <span className="text-sm text-gray-500">5%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-purple-500 h-2.5 rounded-full"
                        style={{ width: '5%' }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      Average Time to Conversion
                    </span>
                    <span className="text-lg font-semibold">6.2 days</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Stats Table */}
            <div className="bg-white rounded-lg shadow border overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Detailed Performance Metrics
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Metric
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Current Period
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Previous Period
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        New Signups
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        168
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        142
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +18.3%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Revenue
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        $7,849
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        $6,980
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +12.4%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Active Users
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        92
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        80
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +15.0%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Churn Rate
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        1.2%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        1.5%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        -0.3%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Portal Count
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        208
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        178
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +16.9%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-center text-gray-500 my-4 mb-8">
              <p>
                Need more detailed analytics?{' '}
                <a
                  href="/admin/reports"
                  className="text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                >
                  <span>Go to Reports</span>{' '}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformAnalytics;
